import React, { useState, useEffect, useRef } from 'react';
import { Star, Shield, Clock, Users, Award, CheckCircle } from 'lucide-react';

const TeamSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const teamValues = [
    {
      icon: Shield,
      title: 'Professionnalisme',
      description: 'Techniciens certifiés et expérimentés',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Clock,
      title: 'Réactivité',
      description: 'Intervention rapide et efficace',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Qualité de service garantie',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Users,
      title: 'Engagement',
      description: 'Dévoués à votre satisfaction',
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const testimonials = [
    {
      name: 'Youness B.',
      role: 'Particulier',
      content: 'Service impeccable ! j\'avais un problème de climatisation , resolus dans 30 min !! .',
      rating: 5,
      avatar: '👨'
    },
    {
      name: 'Youssef Tougani',
      role: 'Société Hydraumac',
      content: 'Le service de Faucon Bleu nous a beaucoup aidés. Avant, on perdait du temps pour l’entretien de nos véhicules. Maintenant, ils viennent directement sur place, c’est rapide et bien fait. On gagne du temps et c’est très pratique pour notre travail.',
      rating: 5,
      avatar: '👩'
    },
    {
      name: ' Youssef ',
      role: 'Société Buropa',
      content: 'Avec Faucon Bleu, plus besoin de se déplacer pour la vidange ou les contrôles. Ils viennent au dépôt ou au bureau, tout se passe bien et à l’heure. C’est un service sérieux qu’on recommande.',
      rating: 5,
      avatar: '👨‍💼'
    }
  ];

  const stats = [
    { number: '15+', label: 'Années d\'expérience' },
    { number: '10', label: 'Techniciens experts' },
    { number: '500+', label: 'Clients satisfaits' },
    { number: '4.9/5', label: 'Note moyenne' }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden"
      itemScope
      itemType="https://schema.org/Organization"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Team background image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{ backgroundImage: "url('/images/team.png')" }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-blue-900/90 to-indigo-900/90" />
        
        {/* Floating tools */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-float text-white text-2xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${4 + Math.random() * 2}s`
              }}
            >
              {['🔧', '🛠️', '⚙️', '🔩'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium mb-6">
            <Users className="w-4 h-4 mr-2" />
            Notre équipe
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6" itemProp="description">
            Des techniciens{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              qualifiés
            </span>
            ,<br />
            réactifs et engagés
          </h2>
          
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Notre équipe de professionnels prend soin de votre véhicule avec expertise 
            et dévouement, où que vous soyez à Casablanca.
          </p>
        </div>

        {/* Values Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {teamValues.map((value, index) => {
            const Icon = value.icon;
            return (
              <div
                key={index}
                className={`
                  group relative p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10
                  transition-all duration-500 hover:bg-white/10 hover:scale-105 hover:shadow-2xl
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                `}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className={`
                  inline-flex p-3 rounded-xl bg-gradient-to-r ${value.color} mb-4
                  transition-transform duration-300 group-hover:scale-110
                `}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                <p className="text-blue-100">{value.description}</p>
                
                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className={`mb-16 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              {stats.map((stat, index) => (
                <div key={index} className="space-y-2">
                  <div className="text-3xl lg:text-4xl font-bold text-white">{stat.number}</div>
                  <div className="text-blue-100">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className={`transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">Ce que disent nos clients</h3>
            <p className="text-blue-100">La satisfaction client au cœur de notre mission</p>
          </div>

          {/* Testimonial Slider */}
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-8 lg:p-12">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`
                    transition-all duration-500 ${
                      index === currentTestimonial
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 absolute inset-0 translate-x-8'
                    }
                  `}
                >
                  {/* Stars */}
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-xl lg:text-2xl text-white text-center mb-8 leading-relaxed">
                    "{testimonial.content}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-2xl">
                      {testimonial.avatar}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-blue-100 text-sm">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonial Navigation */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`
                    w-3 h-3 rounded-full transition-all duration-300
                    ${index === currentTestimonial
                      ? 'bg-white scale-125'
                      : 'bg-white/30 hover:bg-white/60'
                    }
                  `}
                  aria-label={`Voir le témoignage ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl border border-blue-400/30 p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Rejoignez nos clients satisfaits
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Faites confiance à notre équipe d'experts pour l'entretien et la réparation 
              de votre véhicule. Qualité et satisfaction garanties.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://reservation.faucon-bleu.ma/reservation"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Réserver maintenant
              </a>
              
              <a
                href="tel:+212702094593"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-bold rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                Nous contacter
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Faucon Bleu",
            "description": "Des techniciens qualifiés, réactifs et engagés à prendre soin de votre véhicule, où que vous soyez.",
            "url": "https://faucon-bleu.ma",
            "logo": "https://fauconbleu.ma/logo.jpeg",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Casablanca",
              "addressCountry": "MA"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+212702094593",
              "contactType": "Customer Service",
              "availableLanguage": ["French", "Arabic"]
            },
            "employee": {
              "@type": "Person",
              "jobTitle": "Technicien automobile qualifié",
              "description": "Techniciens formés et professionnels pour l'entretien automobile"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services techniques automobiles",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Service mobile",
                    "description": "Intervention à domicile par techniciens qualifiés"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Réparation en atelier",
                    "description": "Services de mécanique et carrosserie en atelier"
                  }
                }
              ]
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(-5px) rotate(-5deg); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default TeamSection;